import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Phone } from "lucide-react";
import SEO from "@/components/SEO";
import { StarRating } from "@/pages/Home";
import { PHONE, PHONE_TEL } from "@/components/PublicLayout";
import { api } from "@/lib/api";

function splitSentences(text) {
  return text.match(/[^.!?]+[.!?]+["']?\s*/g) || [text];
}

function ReviewCard({ r }) {
  const [expanded, setExpanded] = useState(false);
  const sentences = splitSentences(r.text);
  const needsToggle = sentences.length > 2;
  const slug = r.reviewer_name.toLowerCase().replace(/\s+/g, "-");
  return (
    <figure
      className="review-card-quote bg-white border border-slate-200 rounded p-6 flex flex-col shadow-sm"
      data-testid={`review-card-${slug}`}
    >
      <StarRating rating={r.rating} />
      <blockquote className="mt-4 text-sm text-slate-700 leading-relaxed flex-1">
        {needsToggle && !expanded ? `${sentences.slice(0, 2).join("").trim()}…` : r.text}
      </blockquote>
      {needsToggle && (
        <button
          onClick={() => setExpanded(!expanded)}
          className="mt-3 self-start text-sm font-semibold text-[#1A237E] hover:underline"
          data-testid={`review-read-more-${slug}`}
        >
          {expanded ? "Read less" : "Read more"}
        </button>
      )}
      <figcaption className="mt-5 pt-4 border-t border-slate-100 flex items-center justify-between text-sm">
        <span className="font-semibold text-[#1A237E]">{r.reviewer_name}</span>
        <span className="text-slate-500">{r.platform}{r.date ? ` · ${r.date}` : ""}</span>
      </figcaption>
    </figure>
  );
}

export default function Testimonials() {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api
      .get("/reviews")
      .then((res) => setReviews(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  return (
    <div data-testid="testimonials-page">
      <SEO
        title="Testimonials | Mobile Kitchen Hire Surrey Reviews"
        description="Read five-star reviews for our mobile kitchen hire Surrey service. Real customer feedback from Trustpilot and Facebook on our temporary kitchen hire across Surrey."
      />
      <section className="bg-[#F8FAFC] border-b border-slate-200 py-14">
        <div className="max-w-7xl mx-auto px-4">
          <h1 className="text-4xl sm:text-5xl font-bold text-[#1A237E]">Customer Testimonials</h1>
          <p className="mt-5 text-lg text-slate-600 max-w-3xl leading-relaxed">
            Don't just take our word for it — here's what families across Surrey say about our mobile kitchen
            hire and temporary kitchen hire service.
          </p>
        </div>
      </section>

      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          {loading ? (
            <p className="text-center text-slate-500" data-testid="reviews-loading">Loading reviews…</p>
          ) : (
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
              {reviews.map((r) => (
                <ReviewCard key={r.id} r={r} />
              ))}
            </div>
          )}
        </div>
      </section>

      <section className="bg-[#1A237E] py-16" data-testid="testimonials-cta">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <h2 className="text-2xl sm:text-3xl font-bold text-white">Join Our Happy Customers</h2>
          <p className="mt-4 text-lg text-slate-300 max-w-2xl mx-auto">
            Experience Surrey's five-star temporary kitchen hire service for yourself.
          </p>
          <div className="mt-8 flex flex-wrap justify-center gap-4">
            <Link to="/contact" data-testid="testimonials-quote-button" className="px-7 py-3.5 bg-white text-[#1A237E] font-semibold rounded hover:bg-slate-100 transition-colors">
              Get a Free Quote
            </Link>
            <a href={PHONE_TEL} data-testid="testimonials-call-button" className="flex items-center gap-2 px-7 py-3.5 bg-white text-[#1A237E] font-semibold rounded hover:bg-slate-100 transition-colors">
              <Phone className="h-5 w-5" /> Call {PHONE}
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
